/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  MotifButton,
  MotifErrorMessage,
  MotifFormField,
} from "@ey-xd/motif-react";

import { useEffect, useId, useRef, useState } from "react";

import "./UserSearchSelect.scss";
import { MotifIcon } from "@ey-xd/motif-react";
import {
  MotifNavigationIcClose24px,
  IconoirArrowUpTag,
  IconoirPlus,
} from "@ey-xd/motif-icon";
import { UserSearchInfo } from "../../store/models/userModel";
import SearchInput from "../SearchInput/SearchInput";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { getUserSearchData } from "../../store/slices/UsersSlice";

interface ProxyOwner {
  Name: string;
  Email: string;
}

interface UserSearchSelectProps {
  proxyOwners: { Name: string; Email: string }[] | null;
  name: string;
  setUserSearchSelect: React.Dispatch<
    React.SetStateAction<[] | ProxyOwner[] | null>
  >;
  readOnly: boolean;
  callBack?: (type: string) => void;
  placement?: undefined | "top" | "bottom";
}
const UserSearchSelect = ({
  proxyOwners,
  name = "",
  setUserSearchSelect,
  readOnly = false,
  callBack,
  placement,
}: UserSearchSelectProps) => {
  const [userSearch, setUserSearch] = useState<string[]>([]);
  const [userCurrentSearch, setUserCurrentSearch] = useState<string>("");
  const [searchCharCount, setSearchCharCount] = useState(0);
  const [proxyOwnersList, setProxyOwnersList] = useState<ProxyOwner[]>(
    proxyOwners ?? [],
  );
  const [showProxyOwnerSearch, setShowProxyOwnerSearch] = useState(
    proxyOwners && proxyOwners.length > 0 ? false : true,
  );

  const [expandProxyOwnerSearch, setExpandProxyOwnerSearch] = useState(false);
  const [userList, setUserList] = useState<UserSearchInfo[] | null>(null);
  const [userListLoading, setUserListLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const activeRequestRef = useRef<any>(null);

  useEffect(() => {
    setProxyOwnersList(proxyOwners ?? []);
    setUserSearchSelect(proxyOwners);
    setShowProxyOwnerSearch(
      proxyOwners && proxyOwners.length > 0 ? false : true,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proxyOwners]);

  useEffect(() => {
    return () => {
      if (activeRequestRef.current) {
        (activeRequestRef.current as any).abort();
      }
    };
  }, []);

  const clearSearch = () => {
    if (activeRequestRef.current) {
      (activeRequestRef.current as any).abort();
    }
    setUserCurrentSearch("");
    setUserSearch([]);
    setUserList(null);
    setUserListLoading(false);
    setUserSearchError(false);
  };

  const handleSearchInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (readOnly) return;
    setUserSearchError(false);
    const inputValue = event.target.value;
    setSearchCharCount(inputValue?.length);
    setUserCurrentSearch(inputValue);
    if (inputValue.trim().length >= 3) {
      try {
        if (activeRequestRef.current) {
          (activeRequestRef.current as any).abort();
        }
        setUserListLoading(true);
        const request = dispatch(
          getUserSearchData({ searchText: inputValue.trim() }),
        );
        activeRequestRef.current = request;
        const response = await request;
        if (response.meta.requestStatus === "rejected") {
          if (response.meta.aborted) return;

          setUserList(null);
          setUserSearchError(true);
        } else {
          setUserList(response.payload || []);
        }
      } catch {
        setUserList(null);
      } finally {
        setUserListLoading(false);
      }
    }
  };

  const handleUserClick = (userData: string) => {
    if (!userData || readOnly) return;
    const newProxyOwner = {
      Name: userData.split("(")[0].trim(),
      Email: userData.split("(")[1].split(")")[0].trim(),
    };

    setProxyOwnersList([...proxyOwnersList, newProxyOwner]);
    setUserSearchSelect([...proxyOwnersList, newProxyOwner]);
    setUserSearch([...userSearch, userCurrentSearch]);
    callBack && callBack("add");
    setUserCurrentSearch("");
    setUserList(null);
    setUserListLoading(false);
    setShowProxyOwnerSearch(false);
  };

  const handleDeleteClick = (event: any, email: string) => {
    if (readOnly) return; // Prevent input change if readOnly is true
    event.stopPropagation();
    if (proxyOwnersList.length === 1) {
      setShowProxyOwnerSearch(true);
      callBack && callBack("remove");
      setUserSearchSelect([]);
    }

    const filetrEmailsList = proxyOwnersList.filter((user) => {
      return user.Email !== email;
    });

    setUserSearchSelect(filetrEmailsList);
    setProxyOwnersList(filetrEmailsList);
  };

  const handleSelectedUserGridClick = () => {
    if (readOnly) return; // Prevent input change if readOnly is true
    setShowProxyOwnerSearch(!showProxyOwnerSearch);
  };

  const filteredItems =
    userList?.filter((userData: any) => {
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
      return emailRegex.test(userData.email);
    }) || [];

  const displayItems = filteredItems.map(
    (userData: any) => `${userData.displayName} (${userData.email})`,
  );

  return (
    <>
      <div className={`user-search`}>
        {proxyOwnersList && proxyOwnersList.length > 0 && (
          <div
            className={`selected-users ${readOnly ? "disabled-selected-users" : ""}`}
            onClick={handleSelectedUserGridClick}
          >
            {proxyOwnersList.map((user, index) => {
              if (index > 1) {
                return (
                  <div
                    key={index}
                    className={`selected-user ${
                      expandProxyOwnerSearch ? "" : "hide"
                    }`}
                  >
                    {user.Name}
                    <span
                      className="remove-user"
                      onClick={(e) => handleDeleteClick(e, user.Email)}
                    >
                      <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                    </span>
                  </div>
                );
              }
              return (
                <div key={index} className="selected-user">
                  {user.Name}
                  <span
                    className="remove-user"
                    onClick={(e) => handleDeleteClick(e, user.Email)}
                  >
                    <MotifIcon iconFunction={MotifNavigationIcClose24px} />
                  </span>
                </div>
              );
            })}
            {proxyOwnersList.length > 2 && (
              <div
                className="selected-user collapsed-grid"
                onClick={(event) => {
                  if (readOnly) return; // Prevent input change if readOnly is true
                  event.stopPropagation();
                  setExpandProxyOwnerSearch(!expandProxyOwnerSearch);
                }}
              >
                {expandProxyOwnerSearch ? (
                  <span>
                    <MotifIcon
                      size="24px"
                      className="collapse-btn"
                      iconFunction={IconoirArrowUpTag}
                    />
                  </span>
                ) : (
                  <MotifButton className="more-users">
                    <MotifIcon iconFunction={IconoirPlus} />
                    {Number(proxyOwnersList.length) - 2}
                  </MotifButton>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      <MotifFormField>
        <div className={!showProxyOwnerSearch ? "hide" : ""}>
          <SearchInput
            aria-label="Search for a user"
            id={`user-search-Global-risk-manager-${useId()}`}
            placeholder="Enter a user's name or email id"
            open={!userListLoading}
            className={`Global-risk-manager-search-input 
              ${searchCharCount >= 3 ? "show-suggestions" : ""}
            ${readOnly ? "disabled-item" : ""}`}
            items={
              userList &&
              userList
                .filter((userData: any) => {
                  if (
                    proxyOwnersList.find(
                      (proxyOwner) => proxyOwner.Email === userData.email,
                    )
                  ) {
                    return false;
                  }

                  const emailRegex =
                    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]*\.*(ey)\.com$/;
                  return emailRegex.test(userData.email);
                })
                .map((userData: any) => {
                  return userData.displayName + " (" + userData.email + ")";
                })
            }
            onChange={(e) => handleSearchInputChange(e)}
            onSelect={(item: any) => handleUserClick(item)}
            visibleOptions={3}
            value={userCurrentSearch}
            name={"user-search-" + name}
            multiple={true}
            clearSearch={clearSearch}
            readOnly={readOnly}
            placement={placement}
          />
          {userCurrentSearch.trim().length > 3 &&
            !userListLoading &&
            userList &&
            !displayItems.length && (
              <MotifErrorMessage
                aria-live="assertive"
                role="alert"
                id="aria-error-primaryList"
              >
                No Results Found
              </MotifErrorMessage>
            )}
          {userSearchError && (
            <MotifErrorMessage
              aria-live="assertive"
              role="alert"
              id="aria-error-primaryList"
            >
              Failed to fetch search user list.
            </MotifErrorMessage>
          )}
        </div>
      </MotifFormField>
    </>
  );
};

export default UserSearchSelect;
